import React, { useState } from 'react';
import useUser from 'hooks/useUser';
import { FullStory } from '@fullstory/browser';

import { useOnboardingStep } from 'hooks/useOnboardingStep';
import { OnboardingStep } from 'modules/types/util';
import { OnboardingMilestone } from 'api/types';
import { AutoPayEnrollment } from './AutoPayEnrollment';
import { AutopayModal, AutoPayStep } from './AutoPayModal/AutopayModal';
import { AutoPayModalSuccess } from './AutoPayModalSuccess';

enum ModalType {
  AUTO_PAY_FORM = 'Auto Pay Form',
  SUCCESS = 'Success',
}

export function OnboardingAutoPay() {
  const [modal, setModal] = useState<ModalType | null>(null);
  const { selectedAgreement, refetch: refetchUser } = useUser();
  const { goToStep, showConfirmAddressStep } = useOnboardingStep();

  const achFailedVerification =
    !!selectedAgreement?.ach_elected && !selectedAgreement?.ach_verified;

  const enrolledInAch = selectedAgreement?.ach_elected && selectedAgreement?.ach_verified;

  const handleGoToNextStep = () => {
    if (showConfirmAddressStep) {
      goToStep(OnboardingStep.CONFIRM_ADDRESS);
    } else if (
      selectedAgreement?.onboarding_milestone === OnboardingMilestone.SYSTEM_INSTALLATION
    ) {
      goToStep(OnboardingStep.SYSTEM_INSTALLATION);
    } else {
      goToStep(OnboardingStep.PRE_INSTALLATION);
    }
  };

  const handleClickNext = () => {
    FullStory('trackEvent', {
      name: 'Onboarding - ACH Enrollment Success: Clicked Next',
      properties: {
        page: 'Status',
        onboardingStep: 'AutoPay',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });

    handleGoToNextStep();
  };

  const handleClickSkipForNow = () => {
    FullStory('trackEvent', {
      name: `Onboarding - ACH ${achFailedVerification ? 'Re-Enrollment' : 'Enrollment'}: Clicked Skip for Now`,
      properties: {
        page: 'Status',
        onboardingStep: 'AutoPay',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });

    handleGoToNextStep();
  };

  const handleClickEnroll = () => {
    setModal(ModalType.AUTO_PAY_FORM);

    FullStory('trackEvent', {
      name: `Onboarding - ACH ${achFailedVerification ? 'Re-Enrollment' : 'Enrollment'}: Clicked Enroll in AutoPay`,
      properties: {
        page: 'Status',
        onboardingStep: 'AutoPay',
        onboardingMilestone: selectedAgreement?.onboarding_milestone,
      },
    });
  };

  const handleCompleteFormModal = () => {
    setModal(ModalType.SUCCESS);
    refetchUser();
  };

  const handleCloseModal = () => {
    setModal(null);
  };

  return (
    <>
      <AutoPayEnrollment
        achFailedVerification={achFailedVerification}
        enrolled={!!enrolledInAch}
        onClickEnroll={handleClickEnroll}
        onClickNext={handleClickNext}
        onClickSkipForNow={handleClickSkipForNow}
      />
      {selectedAgreement && (
        <AutopayModal
          agreement={selectedAgreement}
          isOpen={modal === ModalType.AUTO_PAY_FORM}
          initialStep={
            achFailedVerification ? AutoPayStep.ADD_BANK_ACCOUNT : AutoPayStep.TERMS_AND_CONDITIONS
          }
          onCompleteFormModal={handleCompleteFormModal}
          onCloseModal={handleCloseModal}
        />
      )}

      <AutoPayModalSuccess open={modal === ModalType.SUCCESS} onContinue={handleGoToNextStep} />
    </>
  );
}

export default OnboardingAutoPay;
